/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { BEM } from "@adapptio/lib-components";
import React, { FunctionComponent, useCallback } from "react";
import { IViewInstance, ViewService } from "../../../view/ViewService";
import { ViewContent } from "./ViewContent";
import { useStateWithEvent } from "../../hooks";

export interface IViewProviderProps {
	viewService: ViewService;
}

export const ViewProvider: FunctionComponent<IViewProviderProps> = ({ viewService }) => {
	const viewInstanceList = useStateWithEvent(viewService?.onViewListChanged, () =>
		viewService?.getViewInstanceList()?.slice()
	);
	const currentViewId = useStateWithEvent(viewService?.onCurrentViewChanged, () =>
		viewService?.getCurrentViewId()
	);

	const handleReload = useCallback(
		(viewInstance: IViewInstance) => {
			viewService.reload(viewInstance.id);
		},
		[ viewService ]
	);

	const baseClass = BEM("edt-view", null, {
		"is-default": currentViewId === null
	});

	return (
		<div className={baseClass.toClassName()}>
			{viewInstanceList.map((item) => (
				<ViewContent
					key={item.id}
					viewInstance={item}
					ViewComponent={viewService.getComponent(item.name)}
					isActive={item.id === currentViewId}
					onReload={handleReload}
				/>
			))}
		</div>
	);
};
